<template>
  <div class="subscription-content subscription-offer-selection">
    <header>
      <h2>{{ $t("ETAB.PAGES.SUBSCRIPTION.@TITLE") }}</h2>
    </header>

    <article>
      <div>
        <QTabs v-model="selectedOfferId" dense align="justify" narrow-indicator class="text-black subscription-offer-selection-tabs" mobile-arrows>
          <QTab
            v-for="offer in sortedOffers"
            :key="offer.getId()"
            no-caps
            class="subscription-offer-selection-tab-item"
            :name="offer.getId()"
            :style="tabStyle"
            @click="selectOffer(offer)"
          >
            <QIcon v-if="offer.isSelectedByDefaut()" class="subscription-offer-default" name="verified" size="25px" color="secondary" />
            <h4 class="text-center font-095">
              {{ $tc("ETAB.PAGES.SUBSCRIPTION.@OFFER_MONTH_DURATION", offer.getMonthDuration()) }}
            </h4>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <p class="q-mt-xs q-mb-xs" v-html="$t('ETAB.PAGES.SUBSCRIPTION.@OFFER_PRICE', { amount: offer.getAmountHt() })" />
            <!-- manage subtext and lack thereof -->
            <div class="q-mb-xs">
              <ExtraTag v-if="offer.getSubtext()" style="height: 26px" background="primary" :label="offer.getSubtext()" />
              <!-- blank space the same size as extratag -->
              <div v-else style="height: 26px">&nbsp;</div>
            </div>
          </QTab>
        </QTabs>

        <p class="text-italic text-center q-mt-md">
          {{ $t("ETAB.PAGES.SUBSCRIPTION.@SUB_TITLE") }}
        </p>
      </div>
      <div>
        <QTabPanels v-model="selectedOfferId" animated>
          <QTabPanel v-for="offer in subscriptionsOffers" :key="offer.getId()" :name="offer.getId()">
            <SellingPointsList :selling-points="offer.getDescription()" :picture="offer.getImg()" />
          </QTabPanel>
        </QTabPanels>
      </div>
    </article>

    <footer>
      <div v-for="(btn, index) in selectedOffer.getActionButtons()" :key="index" class="text-center q-my-md">
        <QBtn
          :label="btn.getLabel()"
          no-caps
          class="btn-navigation btn-large"
          :color="btn.isOkBtn() ? 'secondary' : ''"
          :flat="btn.isNotOkBtn()"
          :class="btn.isNotOkBtn() ? 'text-underline' : ''"
          @click="$emit('actionBtn', btn)"
        />
      </div>

      <p class="text-subtitle2 margin-0">
        {{ $t("ETAB.PAGES.SUBSCRIPTION.@FOOTER_SUBTITLE") }}
        <router-link :to="cguRoute">
          {{ $t("ETAB.PAGES.SUBSCRIPTION.@FOOTER_SUBTITLE_CGU_LINK") }}
        </router-link>
      </p>
    </footer>
  </div>
</template>

<script>
  import { ETAB_ROUTES } from "@etab/config";
  import {
    ACTION_SUBSCRIPTION_SELECT_OFFER,
    GETTER_SUBSCRIPTION_GET_SELECTED_OFFER,
    GETTER_SUBSCRIPTION_GET_SUBSCRIPTION_OFFERS,
  } from "@etab/stores/types";
  import { useSubscriptionStore } from "@etab/stores/subscription";
  import { mapStores } from "pinia";
  import SellingPointsList from "@etab/components/subscription/SellingPointsList.component.vue";
  import cloneDeep from "lodash.clonedeep";

  export default {
    components: {
      SellingPointsList,
    },
    emits: ["actionBtn"],
    data() {
      return {
        selectedOfferId: undefined,
      };
    },
    computed: {
      ...mapStores(useSubscriptionStore),
      selectedOffer() {
        return this.subscriptionStore[GETTER_SUBSCRIPTION_GET_SELECTED_OFFER];
      },
      subscriptionsOffers() {
        return this.subscriptionStore[GETTER_SUBSCRIPTION_GET_SUBSCRIPTION_OFFERS];
      },
      tabStyle() {
        // for desktop we ensure the same size for all tabs
        // mobile doesn't care so much
        return this.$q.screen.gt.xs ? `width:${100 / this.subscriptionsOffers.length}%` : "";
      },
      sortedOffers() {
        // we cloneDeep because we cannot mutate computed property subscriptionsOffers
        if (this.$q.screen.gt.xs) {
          return cloneDeep(this.subscriptionsOffers).sort((a, b) => parseFloat(a.getDesktopOrder()) - parseFloat(b.getDesktopOrder()));
        }
        return cloneDeep(this.subscriptionsOffers).sort((a, b) => parseFloat(a.getMobileOrder()) - parseFloat(b.getMobileOrder()));
      },
    },
    watch: {
      subscriptionsOffers(newValue, oldValue) {
        // if an offer was already selected (before moving out of this page), we select it again
        if (this.selectedOffer?.getId() !== undefined) {
          this.selectOffer(this.selectedOffer);
        } else {
          // if no offer already selected, we select the offer by default if any
          const defaultSelectedOffer = newValue.find((offer) => offer.isSelectedByDefaut() === true);
          if (defaultSelectedOffer) {
            this.selectOffer(defaultSelectedOffer);
          }
        }
      },
    },
    created() {
      this.cguRoute = { name: ETAB_ROUTES.account.cgu.name };
    },
    methods: {
      selectOffer(offer) {
        this.selectedOfferId = offer.getId();
        this.subscriptionStore[ACTION_SUBSCRIPTION_SELECT_OFFER](offer);
      },
    },
  };
</script>

<style lang="scss">
  .subscription-offer-selection {
    > article {
      display: flex;
      flex-direction: column;
    }

    .q-tab {
      padding: 5px 0 0;
      &.q-tab--active {
        background-color: $secondary-medium;
        color: white;
        border-radius: 10px;
        .q-icon {
          color: white !important;
        }
      }
      &.q-tab--inactive {
        opacity: 0.75;
      }
    }
    .subscription-offer-default {
      position: absolute;
      right: -20px;
      top: 2px;
      @media screen and (max-width: $breakpoint-sm-min) {
        right: -10px;
      }
    }

    @media screen and (max-width: $breakpoint-sm-min) {
      > article {
        flex-direction: column-reverse;
      }
      .subscription-offer-selection-tabs {
        > div {
          flex-wrap: wrap;
        }
      }
      .subscription-offer-selection-tab-item {
        max-width: 45%;
        margin: 5px;
      }
    }
  }
</style>
